import React, { useState, useEffect } from 'react';
import { IconContext } from "react-icons";
import { VscFoldUp, VscFoldDown } from "react-icons/vsc";
import { Link } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getDatabase, ref, get } from "firebase/database";
import "./History.css";

const History = ({ darkMode }) => {
    const [history, setHistory] = useState({});
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userId, setUserId] = useState(null);
    const [historyExtend, setHistoryExtend] = useState(false);
    const [sessionList, setSessionList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const auth = getAuth();
        onAuthStateChanged(auth, (user) => {
            if (user) {
                setIsAuthenticated(true);
                setUserId(user.uid);
            } else {
                setIsAuthenticated(false);
                setUserId(null);
            }
        });
    }, []);

    useEffect(() => {
        const fetchHistory = async () => {
            setLoading(true);
            const db = getDatabase();
            const newHistory = {};

            try {
                if (isAuthenticated && userId) {
                    // Fetch private sandboxes
                    const privateSnapshot = await get(ref(db, `private-sandboxes/${userId}`));
                    if (privateSnapshot.exists()) {
                        const privateData = privateSnapshot.val();
                        Object.keys(privateData).forEach(sessionId => {
                            newHistory[sessionId] = {
                                sessionId,
                                ...privateData[sessionId],
                                isPrivate: true
                            };
                        });
                    }

                    // Fetch public sandboxes
                    const publicSnapshot = await get(ref(db, 'code-sessions'));
                    if (publicSnapshot.exists()) {
                        const publicData = publicSnapshot.val();
                        Object.keys(publicData).forEach(sessionId => {
                            newHistory[sessionId] = {
                                sessionId,
                                ...publicData[sessionId],
                                isPrivate: false
                            };
                        });
                    }
                } else {
                    // For non-authenticated users, only fetch public sandboxes
                    const publicSnapshot = await get(ref(db, 'code-sessions'));
                    if (publicSnapshot.exists()) {
                        const publicData = publicSnapshot.val();
                        Object.keys(publicData).forEach(sessionId => {
                            newHistory[sessionId] = {
                                sessionId,
                                ...publicData[sessionId],
                                isPrivate: false
                            };
                        });
                    }
                }

                setHistory(newHistory);

                // Convert history object to sessionList array and sort by timestamp
                const sessionsArray = Object.values(newHistory).sort((a, b) => {
                    const aTime = a.lastAccessed ? new Date(a.lastAccessed) : new Date(a.createdon || 0);
                    const bTime = b.lastAccessed ? new Date(b.lastAccessed) : new Date(b.createdon || 0);
                    return bTime - aTime;
                });

                setSessionList(sessionsArray);
            } catch (error) {
                console.error('Error fetching history:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [isAuthenticated, userId]);

    // Add scroll event handling
    useEffect(() => {
        const handleScroll = (e) => {
            if (historyExtend) {
                const historyMenu = document.querySelector('.historyMenu');
                if (historyMenu) {
                    e.preventDefault();
                    historyMenu.scrollTop += e.deltaY;
                }
            }
        };

        if (historyExtend) {
            document.addEventListener('wheel', handleScroll, { passive: false });
        }

        return () => {
            document.removeEventListener('wheel', handleScroll);
        };
    }, [historyExtend]);

    const toggleHistory = () => {
        setHistoryExtend(!historyExtend);
    };

    const renderHistoryIcon = () => {
        const icon = historyExtend ? <VscFoldDown /> : <VscFoldUp />;
        return (
            <div className="historyIcon" onClick={toggleHistory}>
                <IconContext.Provider value={{ size: "0.75em", style: { paddingRight: "0.2rem", marginTop: "0.1rem" } }}>
                    {icon}
                </IconContext.Provider>
            </div>
        );
    };

    const renderHistoryItem = (item) => {
        const { sessionId, createdon, language, status, lastAccessed, isPrivate } = item;
        // Ensure timestamps are strings
        const displayTimestamp =
            typeof lastAccessed === 'string'
                ? lastAccessed
                : typeof createdon === 'string'
                    ? createdon
                    : '';
        return (
            <div key={sessionId} className="history-item">
                <Link to={`/box/${sessionId}`} className="history-link">
                    <div className="history-header">
                        <span className="history-url">
                            {isPrivate ? '🔒 ' : ''}interviewsandbox.com/box/{sessionId}
                        </span>
                        <span className="history-timestamp">{displayTimestamp}</span>
                    </div>
                    <div className="history-details">
                        <div className="history-language">
                            <span className="history-label">Language:</span>
                            <span className="history-value">{language || 'Unknown'}</span>
                        </div>
                        <div className="history-status">
                            <span className="history-label">Status:</span>
                            <span className={`history-value status-${status ? status.toLowerCase() : 'no-execution'}`}>{status || 'No execution'}</span>
                        </div>
                    </div>
                </Link>
            </div>
        );
    };

    return (
        <div className={`history ${darkMode ? 'history-dark' : ''}`}>
            <div>
                {renderHistoryIcon()}
            </div>
            <div className={historyExtend ? "historyMenu" : "historyMenuClose"}>
                {loading && <div className="history-loading">Loading...</div>}
                {historyExtend && !loading && sessionList.length === 0 && (
                    <div className="history-empty">No sandboxes found.</div>
                )}
                {historyExtend && !loading && sessionList.map(renderHistoryItem)}
            </div>
        </div>
    );
};

export default History;